import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import {
  PlayIcon,
  StopIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ChevronUpIcon,
  XMarkIcon,
  PencilSquareIcon,
  Cog6ToothIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

/**
 * Component to display job execution results in a readable format
 */
const ResultDisplay = ({ result }) => {
  if (!result) return <span className="text-gray-400">-</span>;
  
  // If result is a string, display it directly
  if (typeof result === 'string') {
    return <span className="text-gray-700">{result}</span>;
  }
  
  // If it's an object, extract key metrics
  if (typeof result === 'object') {
    const metrics = [];
    
    // Common result fields
    if (result.accountsProcessed !== undefined) {
      metrics.push(`${result.accountsProcessed} accounts`);
    }
    if (result.totalSitesUnpublished !== undefined) {
      metrics.push(`${result.totalSitesUnpublished} sites unpublished`);
    }
    if (result.websitesChecked !== undefined) {
      metrics.push(`${result.websitesChecked} checked`);
    }
    if (result.websitesRetried !== undefined && result.websitesRetried > 0) {
      metrics.push(`${result.websitesRetried} retried`);
    }
    if (result.emailsSent !== undefined) {
      metrics.push(`${result.emailsSent} emails sent`);
    }
    if (result.sessionsDeleted !== undefined || result.deletedCount !== undefined) {
      const count = result.sessionsDeleted ?? result.deletedCount ?? 0;
      metrics.push(`${count} cleaned up`);
    }
    if (result.versionsDeleted !== undefined) {
      metrics.push(`${result.versionsDeleted} versions deleted`);
    }
    if (result.websitesProcessed !== undefined) {
      metrics.push(`${result.websitesProcessed} websites`);
    }
    if (result.cancelled !== undefined) {
      metrics.push(`${result.cancelled} cancelled`);
    }
    if (result.jobsCleaned !== undefined) {
      metrics.push(`${result.jobsCleaned} jobs cleaned`);
    }
    if (result.entriesRemoved !== undefined) {
      metrics.push(`${result.entriesRemoved} entries removed`);
    }
    if (result.skipped) {
      return <span className="text-yellow-600">Skipped: {result.reason || 'already running'}</span>;
    }
    if (result.errors && result.errors.length > 0) {
      metrics.push(`${result.errors.length} errors`);
    }
    
    // If we found metrics, display them
    if (metrics.length > 0) {
      return (
        <span className="text-gray-700">
          {metrics.join(' • ')}
        </span>
      );
    }
    
    // Fallback: show JSON for unknown structures
    const jsonStr = JSON.stringify(result);
    if (jsonStr.length > 100) {
      return (
        <span className="text-gray-500 text-xs font-mono truncate block max-w-xs" title={jsonStr}>
          {jsonStr.substring(0, 100)}...
        </span>
      );
    }
    return <span className="text-gray-500 text-xs font-mono">{jsonStr}</span>;
  }
  
  return <span className="text-gray-400">-</span>;
};

// Common cron presets
const CRON_PRESETS = [
  { label: 'Every minute', value: '* * * * *', description: 'Runs every minute' },
  { label: 'Every 5 minutes', value: '*/5 * * * *', description: 'Runs every 5 minutes' },
  { label: 'Every 10 minutes', value: '*/10 * * * *', description: 'Runs every 10 minutes' },
  { label: 'Every 15 minutes', value: '*/15 * * * *', description: 'Runs every 15 minutes' },
  { label: 'Every 30 minutes', value: '*/30 * * * *', description: 'Runs every 30 minutes' },
  { label: 'Every hour', value: '0 * * * *', description: 'Runs at the start of every hour' },
  { label: 'Every 2 hours', value: '0 */2 * * *', description: 'Runs every 2 hours' },
  { label: 'Every 6 hours', value: '0 */6 * * *', description: 'Runs every 6 hours' },
  { label: 'Every 12 hours', value: '0 */12 * * *', description: 'Runs at 00:00 and 12:00' },
  { label: 'Daily at midnight', value: '0 0 * * *', description: 'Runs at 00:00 every day' },
  { label: 'Daily at 6 AM', value: '0 6 * * *', description: 'Runs at 06:00 every day' },
  { label: 'Daily at 9 AM', value: '0 9 * * *', description: 'Runs at 09:00 every day' },
  { label: 'Daily at noon', value: '0 12 * * *', description: 'Runs at 12:00 every day' },
  { label: 'Daily at 6 PM', value: '0 18 * * *', description: 'Runs at 18:00 every day' },
  { label: 'Weekly (Sunday midnight)', value: '0 0 * * 0', description: 'Runs every Sunday at midnight' },
  { label: 'Weekly (Monday 9 AM)', value: '0 9 * * 1', description: 'Runs every Monday at 09:00' },
  { label: 'Monthly (1st at midnight)', value: '0 0 1 * *', description: 'Runs on the 1st of each month' },
  { label: 'Custom', value: 'custom', description: 'Enter a custom cron expression' }
];

/**
 * Translate a cron expression to human-readable text
 */
const translateCronExpression = (cron) => {
  if (!cron) return null;
  
  const parts = cron.split(' ');
  if (parts.length !== 5) return cron;
  
  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;
  
  // Check common patterns
  if (cron === '* * * * *') return 'Every minute';
  if (cron === '0 * * * *') return 'Every hour';
  if (cron === '0 0 * * *') return 'Daily at midnight';
  if (cron === '0 0 * * 0') return 'Weekly on Sunday';
  if (cron === '0 0 1 * *') return 'Monthly on the 1st';
  
  // Every X minutes
  if (minute.startsWith('*/') && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    const interval = minute.slice(2);
    return `Every ${interval} minutes`;
  }
  
  // Every X hours
  if (minute === '0' && hour.startsWith('*/') && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    const interval = hour.slice(2);
    return `Every ${interval} hours`;
  }
  
  // Daily at specific time
  if (dayOfMonth === '*' && month === '*' && dayOfWeek === '*' && !minute.includes('*') && !hour.includes('*')) {
    const h = parseInt(hour);
    const m = parseInt(minute);
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h > 12 ? h - 12 : (h === 0 ? 12 : h);
    return `Daily at ${h12}:${m.toString().padStart(2, '0')} ${period}`;
  }
  
  // Weekly patterns
  if (dayOfMonth === '*' && month === '*' && dayOfWeek !== '*' && !minute.includes('*') && !hour.includes('*')) {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayName = days[parseInt(dayOfWeek)] || dayOfWeek;
    const h = parseInt(hour);
    const m = parseInt(minute);
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h > 12 ? h - 12 : (h === 0 ? 12 : h);
    return `${dayName}s at ${h12}:${m.toString().padStart(2, '0')} ${period}`;
  }
  
  // Monthly patterns
  if (dayOfMonth !== '*' && month === '*' && dayOfWeek === '*' && !minute.includes('*') && !hour.includes('*')) {
    const h = parseInt(hour);
    const m = parseInt(minute);
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h > 12 ? h - 12 : (h === 0 ? 12 : h);
    const daySuffix = (d) => {
      if (d > 3 && d < 21) return 'th';
      switch (d % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    const d = parseInt(dayOfMonth);
    return `Monthly on the ${d}${daySuffix(d)} at ${h12}:${m.toString().padStart(2, '0')} ${period}`;
  }
  
  // Return original if no pattern matched
  return cron;
};

const Jobs = () => {
  const [jobs, setJobs] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [runningActions, setRunningActions] = useState({});
  const [editingJob, setEditingJob] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [cronMode, setCronMode] = useState('preset'); // 'preset' or 'custom'
  const isInitialMount = useRef(true);

  const fetchJobsStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/jobs/status`);
      setJobs(response.data.data);
    } catch (error) {
      console.error('Error fetching jobs status:', error);
      // Only show toast after initial load to avoid confusing users on page load errors
      if (!isInitialMount.current) {
        toast.error('Failed to fetch jobs status');
      }
    } finally {
      setLoading(false);
      isInitialMount.current = false;
    }
  }, []);

  useEffect(() => {
    fetchJobsStatus();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchJobsStatus, 30000);
    return () => clearInterval(interval);
  }, [fetchJobsStatus]);

  const handleRunJob = async (jobId, e) => {
    e.stopPropagation();
    setRunningActions(prev => ({ ...prev, [`run-${jobId}`]: true }));
    try {
      await axios.post(`${API_BASE_URL}/api/admin/jobs/${jobId}/run`);
      toast.success(`Job "${jobs[jobId]?.name}" started`);
      // Refresh status after a short delay
      setTimeout(fetchJobsStatus, 1000);
    } catch (error) {
      console.error('Error running job:', error);
      toast.error(`Failed to run job: ${error.response?.data?.error || error.message}`);
    } finally {
      setRunningActions(prev => ({ ...prev, [`run-${jobId}`]: false }));
    }
  };

  const handleStartScheduler = async (jobId, e) => {
    e.stopPropagation();
    setRunningActions(prev => ({ ...prev, [`start-${jobId}`]: true }));
    try {
      await axios.post(`${API_BASE_URL}/api/admin/jobs/${jobId}/start`);
      toast.success(`Scheduler started for "${jobs[jobId]?.name}"`);
      fetchJobsStatus();
    } catch (error) {
      console.error('Error starting scheduler:', error);
      toast.error(`Failed to start scheduler: ${error.response?.data?.error || error.message}`);
    } finally {
      setRunningActions(prev => ({ ...prev, [`start-${jobId}`]: false }));
    }
  };

  const handleStopScheduler = async (jobId, e) => {
    e.stopPropagation();
    setRunningActions(prev => ({ ...prev, [`stop-${jobId}`]: true }));
    try {
      await axios.post(`${API_BASE_URL}/api/admin/jobs/${jobId}/stop`);
      toast.success(`Scheduler stopped for "${jobs[jobId]?.name}"`);
      fetchJobsStatus();
    } catch (error) {
      console.error('Error stopping scheduler:', error);
      toast.error(`Failed to stop scheduler: ${error.response?.data?.error || error.message}`);
    } finally {
      setRunningActions(prev => ({ ...prev, [`stop-${jobId}`]: false }));
    }
  };

  const handleRowClick = (jobId) => {
    setSelectedJobId(selectedJobId === jobId ? null : jobId);
  };

  const handleEditClick = (jobId, e) => {
    e.stopPropagation();
    const job = jobs[jobId];
    setEditingJob(jobId);
    setEditForm({
      name: job.name || '',
      description: job.description || '',
      schedule: job.schedule || '',
      cronExpression: job.cronExpression || '',
      isEnabled: job.isEnabled !== false,
      timeoutMs: job.timeoutMs || 300000,
      retryCount: job.retryCount || 0,
      retryDelayMs: job.retryDelayMs || 60000
    });
    // Check if cron expression matches a preset
    const matchingPreset = CRON_PRESETS.find(p => p.value === job.cronExpression);
    setCronMode(matchingPreset && matchingPreset.value !== 'custom' ? 'preset' : 'custom');
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleCronPresetChange = (presetValue) => {
    if (presetValue === 'custom') {
      setCronMode('custom');
    } else {
      setCronMode('preset');
      const preset = CRON_PRESETS.find(p => p.value === presetValue);
      if (preset) {
        handleEditFormChange('cronExpression', presetValue);
        handleEditFormChange('schedule', preset.description);
      }
    }
  };

  const handleSaveEdit = async () => {
    if (!editingJob) return;
    
    setSaving(true);
    try {
      await axios.put(`${API_BASE_URL}/api/admin/jobs/${editingJob}/config`, editForm);
      toast.success(`Job "${editForm.name}" updated successfully`);
      setEditingJob(null);
      fetchJobsStatus();
    } catch (error) {
      console.error('Error updating job:', error);
      toast.error(`Failed to update job: ${error.response?.data?.error || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingJob(null);
    setEditForm({});
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatDuration = (ms) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const getStatusBadge = (job) => {
    if (!job.isEnabled) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
          <ExclamationTriangleIcon className="w-3 h-3" />
          Disabled
        </span>
      );
    }
    if (job.isRunning) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
          <ArrowPathIcon className="w-3 h-3 animate-spin" />
          Running
        </span>
      );
    }
    if (job.isScheduled) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
          <CheckCircleIcon className="w-3 h-3" />
          Scheduled
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
        <StopIcon className="w-3 h-3" />
        Stopped
      </span>
    );
  };

  const getExecutionStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
            <CheckCircleIcon className="w-3 h-3" />
            Success
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
            <XCircleIcon className="w-3 h-3" />
            Failed
          </span>
        );
      case 'running':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
            <ArrowPathIcon className="w-3 h-3 animate-spin" />
            Running
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
            {status}
          </span>
        );
    }
  };

  const getScheduleDisplay = (job) => {
    // Try to translate cron expression first
    if (job.cronExpression) {
      const translated = translateCronExpression(job.cronExpression);
      if (translated && translated !== job.cronExpression) {
        return translated;
      }
    }
    // Fall back to schedule description
    return job.schedule || '-';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="py-12">
          <LoadingSpinner />
        </div>
      </AdminLayout>
    );
  }

  const jobsList = Object.entries(jobs);
  const selectedJob = selectedJobId ? jobs[selectedJobId] : null;
  const currentCronPreset = CRON_PRESETS.find(p => p.value === editForm.cronExpression);

  return (
    <AdminLayout>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Scheduled Jobs</h1>
            <p className="mt-1 text-sm text-gray-500">
              Monitor and control background jobs running on the server
            </p>
          </div>
          <button
            onClick={fetchJobsStatus}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Jobs Table */}
        <div className="flex-1 bg-white rounded-xl shadow-md border border-gray-200">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Job Name
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">
                  Status
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-36">
                  Schedule
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-44">
                  Last Run
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-20">
                  History
                </th>
                <th scope="col" className="px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-48">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {jobsList.map(([jobId, job]) => (
                <tr
                  key={jobId}
                  onClick={() => handleRowClick(jobId)}
                  className={`cursor-pointer transition-colors ${
                    selectedJobId === jobId 
                      ? 'bg-primary-50 hover:bg-primary-100' 
                      : 'hover:bg-gray-50'
                  } ${!job.isEnabled ? 'opacity-60' : ''}`}
                >
                  <td className="px-3 py-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-900 truncate">{job.name}</div>
                      <div className="text-xs text-gray-500 truncate">{job.description}</div>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    {getStatusBadge(job)}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1">
                      <ClockIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <span className="text-sm text-gray-900 truncate">{getScheduleDisplay(job)}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="text-sm text-gray-900">{formatDate(job.lastRunAt)}</div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="text-sm text-gray-500">
                      {job.history?.length || 0}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        onClick={(e) => handleEditClick(jobId, e)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                        title="Edit Configuration"
                      >
                        <PencilSquareIcon className="w-3.5 h-3.5" />
                        Edit
                      </button>

                      <button
                        onClick={(e) => handleRunJob(jobId, e)}
                        disabled={runningActions[`run-${jobId}`] || job.isRunning}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title="Run Now"
                      >
                        {runningActions[`run-${jobId}`] ? (
                          <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <PlayIcon className="w-3.5 h-3.5" />
                        )}
                        Run
                      </button>

                      {job.isScheduled ? (
                        <button
                          onClick={(e) => handleStopScheduler(jobId, e)}
                          disabled={runningActions[`stop-${jobId}`]}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Stop Scheduler"
                        >
                          {runningActions[`stop-${jobId}`] ? (
                            <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <StopIcon className="w-3.5 h-3.5" />
                          )}
                          Stop
                        </button>
                      ) : (
                        <button
                          onClick={(e) => handleStartScheduler(jobId, e)}
                          disabled={runningActions[`start-${jobId}`] || !job.isEnabled}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title={!job.isEnabled ? 'Enable job first' : 'Start Scheduler'}
                        >
                          {runningActions[`start-${jobId}`] ? (
                            <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <PlayIcon className="w-3.5 h-3.5" />
                          )}
                          Start
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {jobsList.length === 0 && (
            <div className="text-center py-12">
              <ExclamationTriangleIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">No scheduled jobs found</p>
            </div>
          )}
        </div>

        {/* History Panel */}
        {selectedJob && (
          <div className="mt-4 bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
            {/* Panel Header */}
            <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <ChevronUpIcon className="w-5 h-5 text-gray-400" />
                <span className="font-semibold text-gray-900">
                  Execution History: {selectedJob.name}
                </span>
                <span className="text-sm text-gray-500">
                  ({selectedJob.history?.length || 0} records)
                </span>
              </div>
              <button
                onClick={() => setSelectedJobId(null)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* History Table */}
            <div className="max-h-80 overflow-y-auto">
              {selectedJob.history && selectedJob.history.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">
                        Status
                      </th>
                      <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Started At
                      </th>
                      <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-20">
                        Duration
                      </th>
                      <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">
                        Trigger
                      </th>
                      <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Result / Error
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedJob.history.map((execution, index) => (
                      <tr key={execution.id || index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          {getExecutionStatusBadge(execution.status)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(execution.startedAt)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-600">
                          {formatDuration(execution.durationMs)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {execution.triggeredBy || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {execution.error ? (
                            <span className="text-red-600">{execution.error}</span>
                          ) : execution.result ? (
                            <ResultDisplay result={execution.result} />
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No execution history yet for this job
                </div>
              )}
            </div>
          </div>
        )}

        {/* Info Banner */}
        <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span>
              Click a row to view history • Edit to modify schedule and settings • History retained for 30 days • Auto-refresh every 30s
            </span>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editingJob && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={handleCancelEdit}
            />

            {/* Modal */}
            <div className="relative inline-block w-full max-w-lg p-6 my-8 overflow-hidden text-left align-middle bg-white shadow-xl rounded-2xl transform transition-all">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                    <Cog6ToothIcon className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Edit Job Configuration</h3>
                    <p className="text-sm text-gray-500">{editingJob}</p>
                  </div>
                </div>
                <button
                  onClick={handleCancelEdit}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Form */}
              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => handleEditFormChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => handleEditFormChange('description', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Schedule Section */}
                <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                  <div className="flex items-center gap-2 mb-2">
                    <ClockIcon className="w-5 h-5 text-gray-500" />
                    <span className="font-medium text-gray-700">Schedule</span>
                  </div>

                  {/* Preset Selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Quick Select
                    </label>
                    <select
                      value={currentCronPreset?.value || 'custom'}
                      onChange={(e) => handleCronPresetChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
                    >
                      {CRON_PRESETS.map((preset) => (
                        <option key={preset.value} value={preset.value}>
                          {preset.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Cron Expression (shown when custom or for display) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Cron Expression
                      {cronMode === 'preset' && (
                        <span className="ml-2 text-xs text-gray-400">(auto-generated)</span>
                      )}
                    </label>
                    <input
                      type="text"
                      value={editForm.cronExpression}
                      onChange={(e) => {
                        handleEditFormChange('cronExpression', e.target.value);
                        setCronMode('custom');
                      }}
                      placeholder="e.g., */5 * * * *"
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm ${
                        cronMode === 'preset' ? 'bg-gray-100' : 'bg-white'
                      }`}
                    />
                    <div className="mt-1 flex items-center justify-between">
                      <p className="text-xs text-gray-500">
                        Format: minute hour day month weekday
                      </p>
                      {editForm.cronExpression && (
                        <p className="text-xs text-primary-600 font-medium">
                          → {translateCronExpression(editForm.cronExpression)}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Schedule Description (for display) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Display Text
                      <span className="ml-1 text-xs text-gray-400">(shown in dashboard)</span>
                    </label>
                    <input
                      type="text"
                      value={editForm.schedule}
                      onChange={(e) => handleEditFormChange('schedule', e.target.value)}
                      placeholder="e.g., Every 5 minutes"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                {/* Enabled Toggle */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Job Enabled
                    </label>
                    <p className="text-xs text-gray-500">
                      Disabled jobs won't run on schedule
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleEditFormChange('isEnabled', !editForm.isEnabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      editForm.isEnabled ? 'bg-primary-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        editForm.isEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Advanced Settings */}
                <details className="group">
                  <summary className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                    <svg className="w-4 h-4 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    Advanced Settings
                  </summary>
                  <div className="mt-3 space-y-3 pl-6">
                    {/* Timeout */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Timeout (ms)
                      </label>
                      <input
                        type="number"
                        value={editForm.timeoutMs}
                        onChange={(e) => handleEditFormChange('timeoutMs', parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    {/* Retry Count */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Retry Count
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="10"
                        value={editForm.retryCount}
                        onChange={(e) => handleEditFormChange('retryCount', parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    {/* Retry Delay */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Retry Delay (ms)
                      </label>
                      <input
                        type="number"
                        value={editForm.retryDelayMs}
                        onChange={(e) => handleEditFormChange('retryDelayMs', parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                </details>
              </div>

              {/* Footer */}
              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  onClick={handleCancelEdit}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Jobs;
